// @ts-check
import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Label } from '../../index'
import { map, addIndex, equals, isNil, isEmpty } from 'ramda'
import cn from 'classnames'
import { usePrevious } from '../../../components/utils/hooks'
import styles from './style.css'

const mapIndexed = addIndex(map)

const Option = ({ label, parentRef, onClick, isHovered = false, onOptionHover }) => (
  <div
    className={cn(styles.option, { [styles.isHovered]: isHovered })}
    onClick={onClick}
    onMouseEnter={onOptionHover}
    ref={parentRef}
  >
    {label}
  </div>
)

/**
 * @param {{
 * placeholder: string,
 * label: string,
 * onChange: {(event: React.ChangeEvent | any): void},
 * name: string,
 * options: OptionProps[],
 * value: string,
 * valid: boolean,
 * info: string }} props
 */
const CustomSelect = ({
  placeholder,
  label,
  name,
  onChange,
  options,
  value,
  valid,
  info
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [isOpenUp, setIsOpenUp] = useState(false)
  const [allOptions, setAllOptions] = useState([])
  const [pressedKey, setPressedKey] = useState('')
  const ref = useRef(null)
  const selectRef = useRef(null)
  const listRef = useRef(null)
  const prevAllOptions = usePrevious(allOptions)

  const optionRefs = useMemo(
    () => {
      return Array.from({ length: options.length + 1 }).map(() => React.createRef())
    },
    [options]
  )

  useEffect(() => {
    if (isNil(options) || isEmpty(options)) return
    const convertedOptions = map(({ label, value }) => ({ label, value: value.toString() }))(options)
    setAllOptions([{ value: '', label: placeholder }, ...convertedOptions])
  }, [options])

  useEffect(() => {
    if (allOptions.length > 1 && !equals(prevAllOptions, allOptions) && value && optionRefs[1].current) {
      const selectedIndex = allOptions.findIndex(item => {
        return item.value === value
      })
      if (selectedIndex !== -1) {
        scrollToElement(selectedIndex)
        setSelectedIndex(selectedIndex)
      }
    }
  }, [allOptions, value, optionRefs])

  useEffect(() => {
    function handleClickOutside (event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    function handleScroll () {
      setIsOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)
    document.addEventListener('scroll', handleScroll)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
      document.removeEventListener('mousedown', handleScroll)
    }
  }, [ref])

  useEffect(() => {
    if (!isOpen) {
      scrollToElement(selectedIndex)
    }
  }, [isOpen])

  const scrollToElement = (index) => {
    const scrolToHeight = optionRefs.reduce((acc, optionRef, curIndex) => {
      if (curIndex < index) {
        acc = acc + optionRef.current.clientHeight
      }
      return acc
    }, 0)
    listRef.current.scrollTop = scrolToHeight
  }

  /**
   * @param {{ value: string, label: string }} param
   * @param {number} indx
   */
  const onItemClick = ({ value, label }, indx) => () => {
    setIsOpen(false)
    onChange({ target: { value, label, name } })
    setSelectedIndex(indx)
  }

  const onSelectClick = () => {
    setIsOpen(value => !value)
    setOpenDirection()
  }
  /**
   * @param {string} value
   * @returns {string}
   */
  const getLabelByValue = (value) => {
    const selectedOption = allOptions.find(option => option.value === value)
    return selectedOption?.label
  }

  const onKeyDownEnter = () => {
    if (!isOpen) {
      setOpenDirection()
    }
    setIsOpen(value => !value)
  }

  const onKeyDownArrowDown = () => {
    if (selectedIndex >= allOptions.length - 1) {
      return
    }
    const nextElement = optionRefs[selectedIndex + 1].current
    const nextElementHeight = nextElement.clientHeight
    const parentScrollTop = listRef.current.scrollTop
    const nextElementOffset = nextElement.offsetTop
    const itemTop = nextElementOffset + nextElementHeight - parentScrollTop
    if (itemTop >= listRef.current.clientHeight) {
      listRef.current.scrollTop = parentScrollTop + nextElementHeight
    }
    setSelectedIndex(index => index + 1)
  }

  const onKeyDownArrowUp = () => {
    if (selectedIndex === 0) {
      return
    }
    const itemOffset = optionRefs[selectedIndex].current.offsetTop
    const parentScrollTop = listRef.current.scrollTop
    const prevElementHeight = optionRefs[selectedIndex - 1].current.clientHeight
    if (itemOffset - parentScrollTop <= prevElementHeight) {
      listRef.current.scrollTop = parentScrollTop - prevElementHeight
    }
    setSelectedIndex(index => index - 1)
  }

  const onKeyDownLetter = (key) => {
    const firstIndexWithKey = allOptions.findIndex(({ label }) => {
      return label.substring(0, 1).toLowerCase() === key.toLowerCase()
    })
    if (pressedKey !== key) {
      if (firstIndexWithKey !== -1) {
        setPressedKey(key)
        scrollToElement(firstIndexWithKey)
        setSelectedIndex(firstIndexWithKey)
      }
    } else {
      if (allOptions[selectedIndex + 1].label.substring(0, 1).toLowerCase() === key.toLowerCase()) {
        onKeyDownArrowDown()
      } else {
        scrollToElement(firstIndexWithKey)
        setSelectedIndex(firstIndexWithKey)
      }
    }
  }

  const onKeyDown = (event) => {
    event.preventDefault()

    switch (event.key) {
      case 'Enter':
        onKeyDownEnter()
        break
      case 'ArrowDown':
        onKeyDownArrowDown()
        break
      case 'ArrowUp':
        onKeyDownArrowUp()
        break
      default:
        onKeyDownLetter(event.key)
    }
  }

  const onKeyUp = (event) => {
    event.preventDefault()

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || pressedKey === event.key) {
      onChange({ target: { ...allOptions[selectedIndex], name } })
    }
  }

  const setOpenDirection = () => {
    if (isOpen) return
    const listHeight = listRef.current.clientHeight
    const { top, bottom } = selectRef.current.getClientRects()[0]
    if ((window.innerHeight - bottom) <= listHeight) {
      setIsOpenUp(true)
      listRef.current.style.top = `${top - listRef.current.clientHeight}px`
    } else {
      setIsOpenUp(false)
      listRef.current.style.top = `${top + selectRef.current.clientHeight}px`
    }
  }

  return (
    <>
      <div className={`${styles.field} ${styles.block}`} ref={ref}>
        <div className={styles.inputBlock}>
          <Label htmlFor={name} value={label} valid={valid} info={info} />
          <div className={styles.select}>
            <div
              className={cn(styles.control, { [styles.openSelect]: isOpen })}
              onClick={onSelectClick}
              onKeyUp={onKeyUp}
              onKeyDown={onKeyDown}
              tabIndex={0}
              ref={selectRef}
            >
              <span className={styles.controlInner}>{getLabelByValue(value) || placeholder}</span>
            </div>
            <div
              className={cn(styles.list, { [styles.hidden]: !isOpen }, { [styles.up]: isOpenUp })}
              ref={listRef}
            >
              {mapIndexed((option, indx) =>
                <Option
                  key={option.value}
                  {...option}
                  onClick={onItemClick(option, indx)}
                  isHovered={selectedIndex === indx}
                  parentRef={optionRefs[indx]}
                />
              )(allOptions)}
            </div>
          </div>
        </div>
      </div>
    </>)
}

export { CustomSelect }
